import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { deliveryText, getTableName, paymentText } from "../../../lang/orders";
import { formatNumber } from "../../../middleware/common-functions";
import { toDate } from "../../../middleware/dates";
import { lang } from "../../../middleware/lang";
import { OrderType } from "../../../models/order_model";
import { SalesDBType, PersonSales } from "../../../models/sales_model";
import { GET_ALL_SUBUSERS } from "./queries";

type SalesByPersonProps = {
  sales_data: SalesDBType;
  dashboardPersonData?: PersonSales[];
};

type SubuserOption = {
  id: string;
  name: string;
  lastname: string;
};

const SalesByPerson: React.FC<SalesByPersonProps> = ({
  sales_data,
  dashboardPersonData,
}) => {
  const [selectedUser, setSelectedUser] = useState<string>("");
  const { data: subusersData } = useQuery(GET_ALL_SUBUSERS);

  const filteredOrders = selectedUser
    ? sales_data.orders.filter(
        (order) => order.user && String(order.user.id) === selectedUser
      )
    : sales_data.orders;

  const total = filteredOrders.reduce((sum, order) => sum + order.total, 0);

  return (
    <div className="row mt-3">
      <div className="col-12">
        <div className="form-row mb-3 align-items-end">
          <div className="col-md-4">
            <label className="text-muted">
              {lang("Filtrar por persona", "Filter by person")}
            </label>
            <select
              className="form-control"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">{lang("Todos", "All")}</option>
              {subusersData?.getAllSubusers?.map((user: SubuserOption) => (
                <option key={user.id} value={user.id}>
                  {user.name} {user.lastname}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <p className="mb-1">
              <strong>{filteredOrders.length}</strong>{" "}
              {lang("ordenes", "orders")} &middot;{" "}
              <strong>${formatNumber(total)}</strong> total
            </p>
          </div>
        </div>

        {/* Staff summary cards */}
        {!selectedUser && dashboardPersonData && dashboardPersonData.length > 0 && (
          <div className="person-summary-grid mb-3">
            {dashboardPersonData.map((person) => (
              <div
                key={person.userId}
                className="person-summary-card material-shadows"
                onClick={() => setSelectedUser(String(person.userId))}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <strong>
                    {person.name} {person.lastname}
                  </strong>
                  <span className="badge badge-primary">
                    {person.count} {lang("ordenes", "orders")}
                  </span>
                </div>
                <h5 className="mb-0 mt-1">${formatNumber(person.total)}</h5>
              </div>
            ))}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover light_table show_on_new">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{lang("Fecha", "Date")}</th>
                <th scope="col">{lang("Hora", "Time")}</th>
                <th scope="col">{lang("Persona", "Person")}</th>
                <th scope="col">{lang("Productos", "Products")}</th>
                <th scope="col" className="sm_hidden">
                  {lang("Pago", "Payment")}
                </th>
                <th scope="col" className="sm_hidden">
                  {lang("Mesa", "Table")}
                </th>
                <th scope="col" className="sm_hidden">
                  {lang("Tipo", "Type")}
                </th>
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody id="order_list">
              {filteredOrders.map((item, key) => (
                <Row key={`${key}_${item.id}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

type RowProps = {
  item: OrderType;
};

const Row: React.FC<RowProps> = ({ item }) => {
  const delivery = deliveryText(item.delivery_type);
  const payment = paymentText(item.payment_type);
  const table_name = getTableName(item.table);
  const userName = item.user
    ? `${item.user.name} ${item.user.lastname || ""}`.trim()
    : "—";

  return (
    <tr data-order_id={item.id}>
      <td className="order_id_row">
        <b>
          <Link to={`/order/${item.id}`}>{item.id}</Link>
        </b>
      </td>
      <td>{toDate(item.creation_date, {}, "date")}</td>
      <td>{toDate(item.creation_date, {}, "hour")}</td>
      <td>{userName}</td>
      <td>{item.resume_txt}</td>
      <td className="sm_hidden">{payment.txt}</td>
      <td className="table_row_id sm_hidden">{table_name}</td>
      <td className="sm_hidden">{delivery.txt}</td>
      <td>
        <b>${formatNumber(item.total)}</b>
      </td>
    </tr>
  );
};

export default SalesByPerson;
